<script setup>
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  banner: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  total: {
    type: Number,
    required: true,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
  isDeletingImage: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save', 'delete', 'deleteImage', 'move']);
const { t } = useI18n();

const form = reactive({
  content: '',
  htmlContent: '',
  backgroundColor: '#EFFFE8',
  textColor: '#16720C',
  linkUrl: '',
  linkTarget: '_blank',
  enabled: true,
  imageFile: null,
  imageUrl: '',
});

const fileInput = ref(null);
const submitted = ref(false);
const localImageUrl = ref('');

const revokeLocalImageUrl = () => {
  if (localImageUrl.value) URL.revokeObjectURL(localImageUrl.value);
  localImageUrl.value = '';
};

const syncForm = banner => {
  revokeLocalImageUrl();
  Object.assign(form, {
    content: banner.content || '',
    htmlContent: banner.html_content || '',
    backgroundColor: banner.background_color || '#EFFFE8',
    textColor: banner.text_color || '#16720C',
    linkUrl: banner.link_url || '',
    linkTarget: banner.link_target || '_blank',
    enabled: banner.enabled ?? true,
    imageFile: null,
    imageUrl: banner.image_url || '',
  });
  submitted.value = false;
};

watch(() => props.banner, syncForm, { immediate: true, deep: true });
onBeforeUnmount(revokeLocalImageUrl);

const hasDisplayContent = computed(
  () =>
    form.content.trim() ||
    form.htmlContent.trim() ||
    form.imageFile ||
    form.imageUrl
);

const isLinkUrlValid = computed(() => {
  if (!form.linkUrl.trim()) return true;

  try {
    return ['http:', 'https:'].includes(new URL(form.linkUrl).protocol);
  } catch {
    return false;
  }
});

const contentError = computed(() =>
  submitted.value && !hasDisplayContent.value
    ? t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.ERRORS.CONTENT_REQUIRED')
    : ''
);

const urlError = computed(() =>
  submitted.value && !isLinkUrlValid.value
    ? t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.ERRORS.INVALID_URL')
    : ''
);

const targetOptions = computed(() => [
  {
    value: '_blank',
    label: t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.TARGET.NEW_TAB'),
  },
  {
    value: '_self',
    label: t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.TARGET.CURRENT_PAGE'),
  },
]);

const previewImageUrl = computed(() => localImageUrl.value || form.imageUrl);

const openFilePicker = () => fileInput.value?.click();

const handleImageUpload = event => {
  const [file] = event.target.files;
  if (!file) return;

  revokeLocalImageUrl();
  form.imageFile = file;
  localImageUrl.value = URL.createObjectURL(file);
};

const removeImage = () => {
  if (form.imageFile) {
    revokeLocalImageUrl();
    form.imageFile = null;
    if (fileInput.value) fileInput.value.value = '';
    return;
  }

  emit('deleteImage', props.banner);
};

const save = () => {
  submitted.value = true;
  if (!hasDisplayContent.value || !isLinkUrlValid.value) return;

  emit('save', {
    banner: props.banner,
    values: { ...form },
  });
};
</script>

<template>
  <article class="rounded-xl border border-n-weak bg-n-surface-1 p-5 shadow-sm">
    <div class="mb-5 flex items-center justify-between gap-3">
      <div class="flex min-w-0 items-center gap-3">
        <span
          class="flex size-8 flex-shrink-0 items-center justify-center rounded-lg bg-n-alpha-2 text-sm font-semibold text-n-slate-12"
        >
          {{ index + 1 }}
        </span>
        <div class="min-w-0">
          <h4 class="m-0 truncate text-base font-medium text-n-slate-12">
            {{
              form.content || $t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.UNTITLED')
            }}
          </h4>
          <p class="m-0 text-xs text-n-slate-11">
            {{
              form.enabled
                ? $t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.ENABLED')
                : $t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.DISABLED')
            }}
          </p>
        </div>
      </div>

      <div class="flex flex-shrink-0 items-center gap-2">
        <Button
          icon="i-lucide-chevron-up"
          color="slate"
          variant="ghost"
          size="sm"
          :disabled="!banner.id || index === 0"
          :aria-label="$t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.MOVE_UP')"
          @click="emit('move', index, -1)"
        />
        <Button
          icon="i-lucide-chevron-down"
          color="slate"
          variant="ghost"
          size="sm"
          :disabled="!banner.id || index === total - 1"
          :aria-label="$t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.MOVE_DOWN')"
          @click="emit('move', index, 1)"
        />
        <Switch v-model="form.enabled" />
      </div>
    </div>

    <div class="grid gap-5 xl:grid-cols-[minmax(0,1fr)_18rem]">
      <div class="grid min-w-0 gap-4">
        <Input
          v-model="form.content"
          :label="$t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.CONTENT')"
          :placeholder="
            $t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.CONTENT_PLACEHOLDER')
          "
          :message="contentError"
          :message-type="contentError ? 'error' : 'info'"
          maxlength="500"
        />

        <TextArea
          v-model="form.htmlContent"
          :label="$t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.HTML_CONTENT')"
          :placeholder="
            $t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.HTML_PLACEHOLDER')
          "
          :max-length="5000"
          show-character-count
          resize
          min-height="7rem"
        />

        <div class="grid gap-4 md:grid-cols-2">
          <Input
            v-model="form.linkUrl"
            :label="$t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.URL')"
            placeholder="https://example.com/promotion"
            :message="urlError"
            :message-type="urlError ? 'error' : 'info'"
          />
          <div class="flex flex-col gap-1">
            <label class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ $t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.TARGET.LABEL') }}
            </label>
            <Select v-model="form.linkTarget" :options="targetOptions" />
          </div>
        </div>

        <div class="grid gap-4 md:grid-cols-2">
          <div class="flex flex-col gap-2">
            <label class="text-heading-3 text-n-slate-12">
              {{ $t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.BACKGROUND_COLOR') }}
            </label>
            <ColorPicker v-model="form.backgroundColor" />
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-heading-3 text-n-slate-12">
              {{ $t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.TEXT_COLOR') }}
            </label>
            <ColorPicker v-model="form.textColor" />
          </div>
        </div>

        <div class="flex flex-wrap items-center justify-end gap-2 pt-1">
          <Button
            :label="$t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.DELETE')"
            color="ruby"
            variant="ghost"
            @click="emit('delete', banner)"
          />
          <Button
            :label="$t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.SAVE')"
            :is-loading="isSaving"
            @click="save"
          />
        </div>
      </div>

      <aside class="flex min-w-0 flex-col gap-4">
        <div class="flex flex-col gap-2">
          <label class="text-heading-3 text-n-slate-12">
            {{ $t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.IMAGE') }}
          </label>
          <input
            ref="fileInput"
            type="file"
            class="hidden"
            accept="image/jpeg,image/png,image/gif,image/webp"
            @change="handleImageUpload"
          />
          <div
            class="flex min-h-36 items-center justify-center overflow-hidden rounded-xl border border-dashed border-n-strong bg-n-alpha-1"
          >
            <img
              v-if="previewImageUrl"
              :src="previewImageUrl"
              :alt="form.content"
              class="max-h-52 w-full object-contain"
            />
            <Button
              v-else
              icon="i-lucide-image-plus"
              color="slate"
              variant="ghost"
              :label="$t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.CHOOSE_IMAGE')"
              @click="openFilePicker"
            />
          </div>
          <div v-if="previewImageUrl" class="flex items-center gap-2">
            <Button
              color="slate"
              variant="outline"
              size="sm"
              :label="$t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.REPLACE_IMAGE')"
              @click="openFilePicker"
            />
            <Button
              color="ruby"
              variant="ghost"
              size="sm"
              :is-loading="isDeletingImage"
              :label="$t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.REMOVE_IMAGE')"
              @click="removeImage"
            />
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <label class="text-heading-3 text-n-slate-12">
            {{ $t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.PREVIEW') }}
          </label>
          <div
            class="overflow-hidden rounded-xl px-4 py-3 shadow-sm"
            :style="{
              backgroundColor: form.backgroundColor,
              color: form.textColor,
            }"
          >
            <img
              v-if="previewImageUrl"
              :src="previewImageUrl"
              :alt="form.content"
              class="mb-2 max-h-48 w-full rounded-lg object-contain"
            />
            <p v-if="form.content" class="m-0 text-sm font-medium">
              {{ form.content }}
            </p>
            <div
              v-if="form.htmlContent"
              v-dompurify-html:chat-banner="form.htmlContent"
              class="mt-1 break-words text-sm"
            />
            <p
              v-if="!hasDisplayContent"
              class="m-0 text-center text-sm opacity-70"
            >
              {{ $t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.PREVIEW_EMPTY') }}
            </p>
          </div>
        </div>
      </aside>
    </div>
  </article>
</template>
